import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  ButtonBack,
  ButtonBackProps,
  ButtonNext,
  ButtonNextProps,
  CarouselContext,
  CarouselProvider,
  CarouselProviderProps,
  Dot,
  Slide,
  Slider,
} from "pure-react-carousel";

import {
  css,
  EmotionComponent,
  styled,
  themeGet,
  useMedia,
} from "@catchoftheday/theme";
import { useWindowSize } from "@catchoftheday/utilities";

import { Box, BoxProps } from "../Box";
import { Flex } from "../Flex";
import { IconArrow, IconProps } from "../Icon";

import { CarouselStyles } from "./Carousel.styles";
import { CarouselWatcherDecorated } from "./CarouselWatcher";

type ButtonOffset = [number, number];

type SlidesEvent = {
  currentIndex: number;
  slidesPerView: number;
  direction: "forward" | "backward";
};

type CarouselInnerProps = {
  /** Accessible name */
  carouselName?: string;
  /** Array of slide elements*/
  slides: React.ReactNode[];
  /** Show or hide the previous/next buttons */
  showNavButtons?: boolean;
  /** Show the bullet nav at bottom of carousel */
  showBullets?: boolean;
  /** Custom x and y position of the previous/next buttons */
  buttonOffset?: ButtonOffset;
  /** On slide load/change */
  onSlideChange?: (event: SlidesEvent) => void;
  /** On carousel initialisation */
  onCarouselInit?: (slidesPerView: number) => void;
  /** Custom styles for previous button */
  previousButtonStyles?: CSSProperties;
  /** Custom styles for next button */
  nextButtonStyles?: CSSProperties;
  /** Custom styles for arrow within next/previous buttons */
  arrowStyles?: IconProps;
  /** Allows consumer to disable nav buttons as needed */
  disableNavButtons?: {
    next?: boolean;
    previous?: boolean;
  };
};

export type CarouselProps = CarouselInnerProps & {
  /** Number of slides to show per breakpoint, largest to smallest*/
  slidesPerBreakpoint?: [number, number, number, number, number];
  /** Auto animate the carousel*/
  autoPlay?: boolean;
  /** Time between auto slides*/
  interval?: number;
  /** Number of slides per previous/next click. Will default to `slidesPerBreakpoint`*/
  step?: number;
  /** Number of slides per drag navigation. Will default to `slidesPerBreakpoint`*/
  dragStep?: number;
  /** Allow user to drag the Carousel items using the mouse */
  dragEnabled?: boolean;
  /** Tiles will loop infinitely*/
  infinite?: boolean;
  /** Lets you have a vertical carousel. Will default to horizontal.*/
  isVertical?: boolean;
  /** Optional fixed width value for slide. Required for vertical nav.*/
  slideWidth?: number;
  /** Optional fixed height value for slide. Required for vertical nav.*/
  slideHeight?: number;
  /** Only display the buttons on hover */
  showNavButtonsOnHover?: boolean;
  /** optional container props */
  containerProps?: BoxProps;
  /** Allows user to touch and move Carousel items on mobile */
  touchEnabled?: boolean;
  /** Slide to specific item. Will default to 0 */
  currentSlide?: number;
};

const excludeProps = [
  "buttonOffset",
  "showNavButtonsOnHover",
  "isVertical",
  "previousButtonStyles",
  "nextButtonStyles",
  "showNavButtons",
  "requiresScroll",
];
const shouldForwardProp = (prop) => !excludeProps.includes(prop);

const buttonStyles = css`
  width: 35px;
  height: 50px;
  text-indent: 50px;
  position: absolute;
  overflow: hidden;
  background-color: rgba(221, 221, 221, 0.5);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  &[disabled] {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

type PreviousButtonProps = {
  buttonOffset: ButtonOffset;
  isVertical: boolean;
  previousButtonStyles?: CSSProperties;
} & ButtonBackProps;

const PreviousButton: EmotionComponent<PreviousButtonProps> = styled(
  ButtonBack,
  { shouldForwardProp }
)<PreviousButtonProps>`
  ${buttonStyles}
  left: ${(props) => props.buttonOffset[0]}px;
  top: calc(50% - 25px + ${(props) => props.buttonOffset[1]}px);
  ${(props) => props.isVertical && `top: 0px; right: 0px;`}
  border-radius: ${(props) => (props.isVertical ? 0 : `0 3px 3px 0`)};
  ${(props) =>
    css({
      ...props.previousButtonStyles,
    })};
`;

type NextButtonProps = {
  buttonOffset: ButtonOffset;
  isVertical: boolean;
  nextButtonStyles?: CSSProperties;
} & ButtonNextProps;

const NextButton: EmotionComponent<NextButtonProps> = styled(ButtonNext, {
  shouldForwardProp,
})<NextButtonProps>`
  ${buttonStyles}
  right: ${(props) => props.buttonOffset[0]}px;
  top: calc(50% - 25px + ${(props) => props.buttonOffset[1]}px);
  ${(props) => props.isVertical && `top: auto; bottom: 0px; right: 0px;`}
  border-radius: ${(props) => (props.isVertical ? 0 : `3px 0 0 3px`)};
  ${(props) =>
    css({
      ...props.nextButtonStyles,
    })}
`;

type CarouselContainerProps = {
  children: React.ReactNode | Element;
} & Partial<CarouselProps> &
  CarouselProviderProps & { requiresScroll: boolean };

const CarouselContainer: EmotionComponent<CarouselContainerProps> = styled(
  CarouselProvider,
  {
    shouldForwardProp,
  }
)<CarouselContainerProps>`
  position: relative;
  ${(props) =>
    props.showNavButtons &&
    (props.orientation == "vertical"
      ? `
  padding-top: 30px;
  padding-bottom: 30px;
  height: 100%;
  `
      : `
  padding-left: 30px;
  padding-right: 30px;
  `)}
  ${(props) =>
    !props.requiresScroll &&
    `
  .carousel__slider-tray--vertical {
    transform: none !important;
  }
  .carousel__slider-tray--horizontal {
    transform: none !important;
  }
  `}
  ${(props) =>
    props.showNavButtonsOnHover &&
    `
      > button {
        display: none;
      }
      &:hover > button {
        display: flex;
      }
  `}
  ${(props) =>
    props.orientation == "vertical" &&
    `
  .carousel__inner-slide {
      position: absolute;
    }
  `}
  .slide-height {
    height: auto;
  }
  .carousel__slide-focus-ring {
    outline: none !important;
  }
`;

const Bullet = styled(Dot)`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  opacity: 0.3;
  background: ${themeGet("colors.typography.subText")};
  margin: 0 4px;
  border: none;
  padding-left: 6px; // explicitly set this to prevent Safari mobile browser making it look stretched
  padding-right: 6px;
  &[disabled] {
    cursor: not-allowed;
    opacity: 0.3;
  }
  &.carousel__dot--selected {
    opacity: 1;
  }
`;

export const Carousel: React.FC<CarouselProps> = ({
  slides,
  slidesPerBreakpoint = [1, 1, 1, 1, 1],
  autoPlay = false,
  interval = 5000,
  infinite = false,
  showNavButtons = true,
  showNavButtonsOnHover = false,
  showBullets = false,
  buttonOffset,
  carouselName = "Carousel",
  dragEnabled = true,
  slideWidth = 0,
  slideHeight = 0,
  isVertical,
  step,
  dragStep,
  containerProps,
  onSlideChange,
  onCarouselInit,
  previousButtonStyles,
  nextButtonStyles,
  arrowStyles,
  disableNavButtons,
  touchEnabled = true,
  currentSlide,
}) => {
  const visibleSlides = useMedia<number>(
    [
      "(min-width: 1280px)",
      "(min-width: 1024px)",
      "(min-width: 768px)",
      "(min-width: 320px)",
      "(min-width: 0px)",
    ],
    slidesPerBreakpoint
  );

  const requiresScroll = !!visibleSlides && slides.length > visibleSlides;

  return (
    <Box {...containerProps}>
      <CarouselContainer
        naturalSlideWidth={slideWidth}
        naturalSlideHeight={slideHeight}
        isIntrinsicHeight={!isVertical}
        totalSlides={slides.length}
        visibleSlides={visibleSlides}
        isPlaying={requiresScroll && autoPlay}
        interval={interval}
        infinite={infinite}
        step={step || visibleSlides}
        dragStep={dragStep || visibleSlides}
        showNavButtons={requiresScroll && showNavButtons}
        showNavButtonsOnHover={showNavButtonsOnHover}
        dragEnabled={dragEnabled}
        touchEnabled={touchEnabled}
        orientation={isVertical ? "vertical" : undefined}
        requiresScroll={requiresScroll}
        currentSlide={currentSlide}
      >
        <CarouselInner
          carouselName={carouselName}
          slides={slides}
          showNavButtons={requiresScroll && showNavButtons}
          visibleSlides={visibleSlides}
          showBullets={showBullets && requiresScroll}
          buttonOffset={buttonOffset}
          onSlideChange={onSlideChange}
          onCarouselInit={onCarouselInit}
          isVertical={isVertical}
          previousButtonStyles={previousButtonStyles}
          nextButtonStyles={nextButtonStyles}
          arrowStyles={arrowStyles}
          disableNavButtons={disableNavButtons}
        />
      </CarouselContainer>
    </Box>
  );
};

const CarouselInner: React.FC<
  CarouselInnerProps & {
    visibleSlides?: number;
    isVertical?: boolean;
  }
> = ({
  carouselName,
  slides,
  showNavButtons,
  visibleSlides,
  showBullets,
  buttonOffset = [0, 0],
  onSlideChange,
  onCarouselInit,
  isVertical = false,
  previousButtonStyles,
  nextButtonStyles,
  arrowStyles,
  disableNavButtons,
}) => {
  const lastIndex = useRef(0);
  const carouselContext = useContext(CarouselContext);

  const windowWidth = useWindowSize().width;
  const slideGroups = visibleSlides
    ? Math.ceil(slides.length / visibleSlides)
    : slides.length;

  const onChange = useCallback(() => {
    const slideEvent: SlidesEvent = {
      currentIndex: carouselContext.state.currentSlide,
      slidesPerView: carouselContext.state.visibleSlides,
      direction:
        carouselContext.state.currentSlide > lastIndex.current
          ? "forward"
          : "backward",
    };
    if (lastIndex.current !== carouselContext.state.currentSlide) {
      onSlideChange?.(slideEvent);
    }
    lastIndex.current = carouselContext.state.currentSlide;
  }, [carouselContext.state.currentSlide, onSlideChange]);

  useEffect(() => {
    carouselContext.subscribe(onChange);
    return () => carouselContext.unsubscribe(onChange);
  }, [carouselContext, onChange]);

  useEffect(() => {
    if (visibleSlides && typeof onCarouselInit === "function") {
      onCarouselInit(visibleSlides);
    }
    // Only fire when visibleSlides change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSlides]);

  return (
    <>
      {windowWidth && <CarouselWatcherDecorated windowWidth={windowWidth} />}
      <Slider aria-label={carouselName} trayTag="div" tabIndex={-1}>
        {slides.map((Content, index) => (
          <Slide
            key={`slide-${index}`}
            index={index}
            tag="div"
            className="slide-height"
          >
            {Content}
          </Slide>
        ))}
      </Slider>
      {showNavButtons && !!visibleSlides && slides.length > visibleSlides && (
        <>
          <PreviousButton
            buttonOffset={buttonOffset}
            isVertical={isVertical}
            previousButtonStyles={previousButtonStyles}
            disabled={disableNavButtons?.previous || undefined}
            aria-label="Previous"
          >
            <IconArrow
              title="Previous arrow"
              style={{
                transform: isVertical ? "rotate(90deg)" : "rotate(0deg)",
              }}
              color="typography.inactive"
              width="22px"
              height="22px"
              {...arrowStyles}
            />
          </PreviousButton>
          <NextButton
            buttonOffset={buttonOffset}
            isVertical={isVertical}
            nextButtonStyles={nextButtonStyles}
            disabled={disableNavButtons?.next || undefined}
            aria-label="Next"
          >
            <IconArrow
              title="Next arrow"
              style={{
                transform: isVertical ? "rotate(-90deg)" : "rotate(180deg)",
              }}
              color="typography.inactive"
              width="22px"
              height="22px"
              {...arrowStyles}
            />
          </NextButton>
        </>
      )}
      {showBullets && (
        <Flex justifyContent="center" py="10px">
          {!!visibleSlides &&
            [...Array(slideGroups)].map((_, index) => (
              <Bullet
                slide={index * visibleSlides}
                key={`bullet-${index}`}
                aria-label={`Go to slide ${index * visibleSlides}`}
                disabled={
                  disableNavButtons?.next ||
                  disableNavButtons?.previous ||
                  undefined
                }
              />
            ))}
        </Flex>
      )}
      {CarouselStyles}
    </>
  );
};

Carousel.displayName = "Carousel";

export default Carousel;
